/**
 * ContractPinGate — 계약 관리 진입 전 역할·PIN 게이트.
 *
 * 역할 정책:
 * - 선생님(직원):
 *    · ownerOnly=false (기본, 계약 현황 조회 등): 게이트 통과 — companySeq 스코프라 매장 사업자의 계약을 그대로 본다.
 *    · ownerOnly=true (계약서 수정·활성화·도장 관리 등): "사업자 전용" 안내로 차단.
 * - 사업자(원장):
 *    · PIN 미등록: 풀스크린 안내 + 등록 모달 강제 노출 (취소 불가)
 *    · PIN 등록됨: children 그대로 렌더
 * - 로딩 중: 빈 상태 (페이지 깜빡임 방지)
 */
import { type ReactNode } from "react";
import { useRouter } from "@tanstack/react-router";
import { usePinStatus } from "~/services/business/seal";
import { useBusinessAuthUserInfo } from "~/services/business/auth";
import { PinSetupModal } from "./PinSetupModal";
import { IconLock } from "./icons";

interface Props {
  children: ReactNode;
  /** true 면 사업자(원장) 전용 화면 — 선생님은 진입 차단. (계약서 수정/활성화/도장 관리) */
  ownerOnly?: boolean;
}

export const ContractPinGate = ({ children, ownerOnly = false }: Props) => {
  const { data: userInfo, isLoading: userLoading } = useBusinessAuthUserInfo();
  const { data: pinStatus, isLoading: pinLoading } = usePinStatus();
  const { history } = useRouter();

  if (userLoading || pinLoading) {
    return (
      <div className="flex-1 flex items-center justify-center text-[12px] text-[#999]">
        불러오는 중...
      </div>
    );
  }

  const isStaff = !!userInfo && userInfo.isOwner === false;

  // 선생님(직원) 처리
  if (isStaff) {
    if (!ownerOnly) {
      // 조회 전용 화면은 통과 — PIN 은 사업자 전용이므로 요구하지 않는다.
      return <>{children}</>;
    }
    // 사업자 전용 기능(계약서 수정/활성화/도장 관리)은 선생님 차단
    return (
      <div className="flex-1 min-h-0 overflow-y-auto bg-[#F5F5F5]">
        <div className="flex flex-col items-center justify-center px-6 py-12 max-w-md mx-auto text-center">
          <div className="w-16 h-16 rounded-full bg-[#FFF7E6] flex items-center justify-center mb-4">
            <IconLock className="w-7 h-7 text-[#FF9934]" />
          </div>
          <div className="text-[16px] font-bold text-[#111] mb-1">
            사업자(원장) 전용 기능이에요
          </div>
          <div className="text-[12px] text-[#666] leading-relaxed mb-6">
            계약서 수정·활성화와 도장 관리는 사업자(원장) 계정에서만 할 수
            있어요.
            <br />
            계약서 발송과 현황 조회는 그대로 이용하실 수 있어요.
          </div>
          <button
            type="button"
            onClick={() => {
              history.back();
            }}
            className="text-[12px] text-[#FF9934] underline"
          >
            ← 이전 화면으로
          </button>
        </div>
      </div>
    );
  }

  if (pinStatus?.hasPin) {
    return <>{children}</>;
  }

  return (
    <div className="flex-1 min-h-0 overflow-y-auto bg-[#F5F5F5]">
      <div className="flex flex-col items-center justify-center px-6 py-12 max-w-md mx-auto text-center">
        <div className="w-16 h-16 rounded-full bg-[#FFF7E6] flex items-center justify-center mb-4">
          <IconLock className="w-7 h-7 text-[#FF9934]" />
        </div>
        <div className="text-[16px] font-bold text-[#111] mb-1">
          계약 관리 사용 PIN 등록
        </div>
        <div className="text-[12px] text-[#666] leading-relaxed mb-6">
          계약서 발송과 도장 사용 시 본인 확인을 위한 6자리 PIN 이 필요해요.
          <br />
          처음 한 번만 등록하면 계약 관리 모든 기능을 사용할 수 있어요.
        </div>
        <div className="bg-white border border-[#E9E9E9] rounded-xl p-4 w-full text-left mb-4">
          <div className="text-[12px] font-semibold text-[#111] mb-2">
            📋 PIN은 어디에 쓰이나요?
          </div>
          <ul className="text-[11px] text-[#666] leading-relaxed space-y-1 list-disc pl-4">
            <li>도장 사용 인증 (계약서 자동 날인)</li>
            <li>계약서 발송 시 본인 확인</li>
            <li>5회 잘못 입력하면 일시 잠금</li>
          </ul>
        </div>
        <div className="text-[10px] text-[#BDBDBD] mb-4">
          PIN 분실 시 고객센터로 문의해 주세요. 다른 사용자에게 노출되지 않도록
          주의해주세요.
        </div>
        <button
          type="button"
          onClick={() => {
            history.back();
          }}
          className="text-[11px] text-[#999] underline"
        >
          나중에 등록 → 이전 화면으로
        </button>
      </div>

      {/* PIN 등록 모달 — 닫기 불가, 등록 성공 시에만 게이트 해제 (자동으로 hasPin: true 가 되면 children 렌더) */}
      <PinSetupModal closable={false} />
    </div>
  );
};
